import { useEffect, useMemo, useState } from 'react';
import type { UseFormReturn, UseFormSetValue } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { metricEventFiltersQueryBuilder } from '@gonfalon/dogfood-flags/dogfood-flags';
import { trackMetricsEvent } from '@gonfalon/metrics/analytics';
import { EventDataPreview } from '@gonfalon/metrics/EventDataPreview';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import type { Metric } from '@gonfalon/metrics/types';
import { Button, FieldGroup, Radio, RadioGroup } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { EventQueryField } from './components/EventQueryField';
import { RequiredLabel } from './components/RequiredLabel';
import { TraceField } from './components/TraceField';
import { EventActivityFormField } from './EventActivityFormField';
import { FilterSelectionPopover } from './FilterSelectionPopover';
import type { MetricForm } from './formUtils';
import { defaultContextAttributeFilter, defaultEventPropertyFilter } from './formUtils';
import { MetricDefinitionFormField } from './MetricDefinitionFormField';
import { MetricEventFilters } from './MetricEventFilters';
import { NumericSpanAttributeField } from './MetricEventFilters/NumericSpanAttributeField';
import { canEditWithQueryBuilder } from './MetricEventQueryBuilder/eventQueryConversion';
import { UnitFormField } from './UnitFormField';

import styles from './CreateMetricModal.module.css';

interface CustomMetricKindDetailsProps {
  context: UseFormReturn<MetricForm>;
}

export const CustomMetricKindDetails: React.FC<CustomMetricKindDetailsProps> = ({ context }) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = context;
  const [eventDefaultValue, measureType, analysisType, filters, kind] = useWatch({
    control,
    name: ['eventDefault', 'measureType', 'analysisType', 'filters', 'kind'],
  });

  const eventDefault: 'enabled' | 'disabled' = eventDefaultValue?.disabled ? 'disabled' : 'enabled';
  const hideEventDefault = analysisType === 'percentile';
  const eventKey = useWatch({ control, name: 'eventKey' });
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  // Custom-event metrics use the QueryBuilder path (traces have their own builder). `filters` stays
  // in form state across data-source switches (the EventQueryField Controller no longer unregisters),
  // so the query builder only *renders* for LaunchDarkly-hosted sources — warehouse-native metrics
  // are a backend WIP behind a separate flag, and filters aren't persisted for non-hosted sources.
  const isQueryBuilderCustomMode = metricEventFiltersQueryBuilder() && kind === 'custom';
  const isEventQueryBuilderEnabled = isQueryBuilderCustomMode && isLDHosted(dataSourceKeyFormValue);

  // Gate the flag-on editor behind a "Filter events" reveal button (mirrors legacy). A metric that
  // already has filters shows the editor immediately; a filter arriving after mount opens it too.
  const [showEventFilters, setShowEventFilters] = useState(filters !== undefined);
  useEffect(() => {
    if (filters !== undefined) {
      setShowEventFilters(true);
    }
  }, [filters]);

  // Flag on: preview always shows (instances API takes only the event key). Flag off: keep main's
  // behavior — hide the preview once filters are set.
  const showEventDataPreview = isEventQueryBuilderEnabled
    ? !errors.eventKey
    : !errors.eventKey && filters === undefined;

  const eventDefaultFieldLabel = useMemo(() => {
    return `Choose how to handle units that do not generate ${kind === 'trace' ? 'a matching span' : 'the event'}`;
  }, [kind]);

  return (
    <>
      {kind === 'trace' ? (
        <TraceField context={context} />
      ) : (
        <div
          className={clsx(styles.eventKeyContainer, {
            [styles.eventKeyContainerQueryBuilder]: isEventQueryBuilderEnabled,
          })}
        >
          {isEventQueryBuilderEnabled ? (
            <>
              {/* Flag on: the "Filter events" button reveals the QueryBuilder editor beside the event
               * key. Fall back to the legacy editor only for a stored filter the bar can't round-trip
               * (canEditWithQueryBuilder false) so editing never drops data. */}
              <div className={styles.eventActivityFiltersRow}>
                <EventActivityFormField context={context} />
                {!showEventFilters && (
                  <Button
                    variant="minimal"
                    className={styles.filterButton}
                    isDisabled={!isLDHosted(dataSourceKeyFormValue)}
                    onPress={() => {
                      setShowEventFilters(true);
                      trackMetricsEvent('Metrics Filter Button Clicked', { eventKey });
                    }}
                  >
                    <span className={styles.filterText}>
                      <Icon name="filter-list" /> Filter events
                    </span>
                  </Button>
                )}
              </div>
              {showEventFilters &&
                (canEditWithQueryBuilder(filters) ? (
                  <EventQueryField context={context} />
                ) : (
                  <MetricEventFilters context={context} headerTitle="Filter event by" showInfoTooltip />
                ))}
            </>
          ) : (
            <div className={styles.eventActivityFiltersRow}>
              {/* Flag off: Legacy filtering. the "Filter event by" button seeds `filters`, revealing <MetricEventFilters> below. */}
              <EventActivityFormField context={context} />
              {filters === undefined && (
                <FilterSelectionPopover
                  onSelectContextKind={() => setValue('filters', defaultContextAttributeFilter)}
                  onSelectEventProperty={() => setValue('filters', defaultEventPropertyFilter)}
                  placement="bottom right"
                >
                  <Button
                    variant="minimal"
                    className={styles.filterButton}
                    isDisabled={!isLDHosted(dataSourceKeyFormValue)}
                    onPress={() =>
                      trackMetricsEvent('Metrics Filter Button Clicked', {
                        eventKey,
                      })
                    }
                  >
                    <span className={styles.filterText}>
                      <Icon name="filter-list" /> Filter event by
                    </span>
                  </Button>
                </FilterSelectionPopover>
              )}
            </div>
          )}

          {showEventDataPreview && isLDHosted(dataSourceKeyFormValue) && (
            <EventDataPreview
              eventKey={eventKey}
              eventKind="custom"
              displayEmptyState
              defaultExpanded
              dataSourceKey={dataSourceKeyFormValue}
              eventLimit={5}
            />
          )}
        </div>
      )}

      {/* Trace metrics and the legacy (flag-off) custom path edit `filters` here; the flag-on custom
       * QueryBuilder path renders its own editor above (when hosted) and keeps preserved filters
       * hidden on non-hosted sources — where they're stripped at submit — so it's excluded here. */}
      {filters !== undefined && !isQueryBuilderCustomMode && <MetricEventFilters context={context} />}

      <MetricDefinitionFormField context={context} />
      {measureType === 'value' && (
        <div className={styles.formElementsGroup}>
          {kind === 'trace' && <NumericSpanAttributeField control={control} />}
          <UnitFormField context={context} />
          {!hideEventDefault && (
            <FieldGroup errorMessage={errors.eventDefault?.message} className={styles.labelInputWrapper}>
              <RequiredLabel label={eventDefaultFieldLabel} htmlFor="eventDefault" />
              <EventDefaultPicker
                currentEventDefault={eventDefault}
                setValue={setValue}
                className={styles.eventDefaultPicker}
                metricKind={kind}
              />
            </FieldGroup>
          )}
        </div>
      )}
    </>
  );
};

// NOTE: the enabled | disabled below refers to whether we're supporting a default or not,
// not whether this form field is able to be edited.
interface EventDefaultPickerProps {
  currentEventDefault: 'enabled' | 'disabled';
  setValue: UseFormSetValue<MetricForm>;
  className?: string;
  metricKind: Metric['kind'];
}

const EventDefaultPicker: React.FC<EventDefaultPickerProps> = (props: EventDefaultPickerProps) => {
  function onEventDefaultChange(value: string) {
    if (value === 'disabled') {
      props.setValue('eventDefault', { disabled: true }, { shouldDirty: true });
    } else {
      props.setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
    }
  }
  return (
    <RadioGroup
      className={props.className}
      onChange={onEventDefaultChange}
      value={props.currentEventDefault}
      id="eventDefault"
      aria-label="Choose a default behavior for events"
    >
      <Radio value="enabled">Include units and set the value to 0</Radio>
      <Radio value="disabled">
        Exclude units that generate no {props.metricKind === 'trace' ? 'matching spans' : 'events'}
      </Radio>
    </RadioGroup>
  );
};
